import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { FIRST_PAGE, getNextPageFromMeta } from '@/api/pagination';
import { STALE_TIME } from '@/api/staleTimes';
import { downloadBlob } from '@/utils/download';
import { DEFAULT_MP_PAGE_SIZE } from '../constants/marketPromotion.constants';
import {
  approvalTotalListSchema,
  donationBatchMemberListSchema,
  donationBatchResponseSchema,
  donationBudgetResponseSchema,
  donationCandidateListSchema,
  donationRequisitionResponseSchema,
  requisitionInstituteListSchema,
  marketProjectResponseSchema,
  type ApprovalReportSearch,
  type ApprovalTotal,
  type DonationBatchMember,
  type DonationBatchResponse,
  type DonationBatchSearch,
  type DonationBudgetPayload,
  type DonationBudgetResponse,
  type DonationBudgetSearch,
  type DonationCandidate,
  type DonationRequisitionPayload,
  type DonationRequisitionResponse,
  type DonationRequisitionSearch,
  type RequisitionInstitute,
  type MarketProjectPayload,
  type MarketProjectResponse,
  type MarketProjectSearch,
  type PublishDonationPayload,
} from '../types/marketPromotionP2.types';

const ISO_DATE_LENGTH = 10;
const stamp = (): string => new Date().toISOString().slice(0, ISO_DATE_LENGTH);

export const mpP2QueryKeys = {
  all: () => ['market-promotion-p2'] as const,
  projects: (params: MarketProjectSearch) => [...mpP2QueryKeys.all(), 'projects', params] as const,
  budgets: (params: DonationBudgetSearch) => [...mpP2QueryKeys.all(), 'budgets', params] as const,
  batches: (stage: string, params: DonationBatchSearch) =>
    [...mpP2QueryKeys.all(), 'batches', stage, params] as const,
  batchMembers: (batchId: number | undefined) =>
    [...mpP2QueryKeys.all(), 'batch-members', batchId] as const,
  candidates: (dateFrom: string, dateTo: string) =>
    [...mpP2QueryKeys.all(), 'candidates', dateFrom, dateTo] as const,
  requisitions: (params: DonationRequisitionSearch) =>
    [...mpP2QueryKeys.all(), 'requisitions', params] as const,
  requisitionInstitutes: (thanaId: number | undefined, search: string) =>
    [...mpP2QueryKeys.all(), 'requisition-institutes', thanaId, search] as const,
  totals: (params: ApprovalReportSearch) => [...mpP2QueryKeys.all(), 'totals', params] as const,
};

export const marketProjectsInfiniteQueryOptions = (params: MarketProjectSearch) =>
  infiniteQueryOptions({
    queryKey: mpP2QueryKeys.projects(params),
    queryFn: async ({ pageParam }): Promise<MarketProjectResponse> => {
      const res = await apiClient.get(API.marketPromotion.projects, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_MP_PAGE_SIZE },
      });
      return marketProjectResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export async function createMarketProject(payload: MarketProjectPayload): Promise<void> {
  await apiClient.post(API.marketPromotion.projects, payload);
}

export async function updateMarketProject(
  id: number,
  payload: MarketProjectPayload,
): Promise<void> {
  await apiClient.put(API.marketPromotion.project(id), payload);
}

export async function deleteMarketProject(id: number): Promise<void> {
  await apiClient.delete(API.marketPromotion.project(id));
}

export const donationBudgetsInfiniteQueryOptions = (params: DonationBudgetSearch) =>
  infiniteQueryOptions({
    queryKey: mpP2QueryKeys.budgets(params),
    queryFn: async ({ pageParam }): Promise<DonationBudgetResponse> => {
      const res = await apiClient.get(API.marketPromotion.budgets, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_MP_PAGE_SIZE },
      });
      return donationBudgetResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export async function createDonationBudget(payload: DonationBudgetPayload): Promise<void> {
  await apiClient.post(API.marketPromotion.budgets, payload);
}

export async function updateDonationBudget(
  id: number,
  payload: DonationBudgetPayload,
): Promise<void> {
  await apiClient.put(API.marketPromotion.budget(id), payload);
}

export const donationBatchesInfiniteQueryOptions = (stage: string, params: DonationBatchSearch) =>
  infiniteQueryOptions({
    queryKey: mpP2QueryKeys.batches(stage, params),
    queryFn: async ({ pageParam }): Promise<DonationBatchResponse> => {
      const res = await apiClient.get(API.marketPromotion.donationBatches, {
        params: { ...params, stage, page: pageParam, pageSize: DEFAULT_MP_PAGE_SIZE },
      });
      return donationBatchResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const donationMembersQueryOptions = (batchId: number | undefined) =>
  queryOptions({
    queryKey: mpP2QueryKeys.batchMembers(batchId),
    queryFn: async (): Promise<DonationBatchMember[]> => {
      const url =
        batchId === undefined
          ? API.marketPromotion.allDonationMembers
          : API.marketPromotion.donationBatchMembers(batchId);
      const res = await apiClient.get(url);
      return donationBatchMemberListSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const donationCandidatesQueryOptions = (dateFrom: string, dateTo: string) =>
  queryOptions({
    queryKey: mpP2QueryKeys.candidates(dateFrom, dateTo),
    queryFn: async (): Promise<DonationCandidate[]> => {
      const res = await apiClient.get(API.marketPromotion.donationCandidates, {
        params: { dateFrom, dateTo },
      });
      return donationCandidateListSchema.parse(res.data);
    },
    enabled: dateFrom !== '' && dateTo !== '',
    staleTime: STALE_TIME,
  });

export async function publishDonationBatch(payload: PublishDonationPayload): Promise<void> {
  await apiClient.post(API.marketPromotion.donationBatches, payload);
}

export async function confirmDonationBatch(
  batchId: number,
  isConfirm: boolean,
  comment?: string,
): Promise<void> {
  await apiClient.patch(API.marketPromotion.confirmDonationBatch(batchId), {
    isConfirm,
    comment,
  });
}

export async function disburseDonationBatch(batchId: number, comment?: string): Promise<void> {
  await apiClient.patch(API.marketPromotion.disburseDonationBatch(batchId), { comment });
}

export async function exportDonationMembers(batchId: number): Promise<void> {
  const res = await apiClient.get(API.marketPromotion.donationBatchMembersExport(batchId), {
    responseType: 'blob',
  });
  downloadBlob(res.data as Blob, `${batchId}-DonationPublishedList.xlsx`);
}

export const donationRequisitionsInfiniteQueryOptions = (params: DonationRequisitionSearch) =>
  infiniteQueryOptions({
    queryKey: mpP2QueryKeys.requisitions(params),
    queryFn: async ({ pageParam }): Promise<DonationRequisitionResponse> => {
      const res = await apiClient.get(API.marketPromotion.requisitions, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_MP_PAGE_SIZE },
      });
      return donationRequisitionResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export async function createDonationRequisition(
  payload: DonationRequisitionPayload,
): Promise<void> {
  await apiClient.post(API.marketPromotion.requisitions, payload);
}

export async function updateDonationRequisition(
  id: number,
  payload: DonationRequisitionPayload,
): Promise<void> {
  await apiClient.put(API.marketPromotion.requisition(id), payload);
}

export const requisitionInstitutesQueryOptions = (thanaId: number | undefined, search: string) =>
  queryOptions({
    queryKey: mpP2QueryKeys.requisitionInstitutes(thanaId, search),
    queryFn: async (): Promise<RequisitionInstitute[]> => {
      const res = await apiClient.get(API.marketPromotion.requisitionInstitutes, {
        params: { thanaId, search: search === '' ? undefined : search },
      });
      return requisitionInstituteListSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const approvalTotalsQueryOptions = (params: ApprovalReportSearch) =>
  queryOptions({
    queryKey: mpP2QueryKeys.totals(params),
    queryFn: async (): Promise<ApprovalTotal[]> => {
      const res = await apiClient.get(API.marketPromotion.approvalTotals, { params });
      return approvalTotalListSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export async function exportCashCollection(params: ApprovalReportSearch): Promise<void> {
  const res = await apiClient.get(API.marketPromotion.cashCollectionExport, {
    params,
    responseType: 'blob',
  });
  downloadBlob(res.data as Blob, `cash-collection-${stamp()}.xlsx`);
}
